import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { supabase } from '../services/supabase';
import { format } from 'date-fns';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pointAmount, setPointAmount] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePointAdjust = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    setPointAmount('');
    setAdminNote('');
    setError('');
  };

  const handleSubmitPointAdjust = async () => {
    if (!pointAmount || pointAmount.trim() === '' || isNaN(parseInt(pointAmount))) {
      setError('유효한 포인트를 입력해주세요.');
      return;
    }

    try {
      const amount = parseInt(pointAmount);

      // Re-fetch current points to avoid race condition
      const { data: freshUser } = await supabase
        .from('users')
        .select('points, total_points_earned')
        .eq('id', selectedUser.id)
        .single();
      const newPoints = freshUser.points + amount;

      if (newPoints < 0) {
        setError('포인트는 0보다 작을 수 없습니다.');
        return;
      }

      // Update user points
      const { error: updateError } = await supabase
        .from('users')
        .update({ points: newPoints })
        .eq('id', selectedUser.id);

      if (updateError) throw updateError;

      // Add point history
      const { error: historyError } = await supabase
        .from('point_history')
        .insert({
          user_id: selectedUser.id,
          type: 'admin_adjust',
          amount,
          balance_after: newPoints,
          description: adminNote || '관리자 조정',
        });

      if (historyError) throw historyError;

      setSuccess('포인트가 성공적으로 조정되었습니다.');
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to adjust points:', error);
      setError('포인트 조정에 실패했습니다.');
    }
  };

  // 테스터 지정/해제 — users.is_tester 토글.
  // 이 플래그가 켜진 계정만 tester_* RPC 와 출금 최소금액 우회가 허용된다(서버 검사).
  const handleToggleTester = async (user) => {
    const next = !user.is_tester;
    if (next && !window.confirm(
      `${user.email || user.name} 계정을 테스터로 지정합니다.\n\n` +
      '테스터는 거북알/포인트/응모권을 즉시 지급받을 수 있고, ' +
      '최소 출금 6,000P 제한 없이 출금을 신청할 수 있습니다. ' +
      '일반 사용자에게는 지정하지 마세요.'
    )) {
      return;
    }
    try {
      const { error: updateError } = await supabase
        .from('users')
        .update({ is_tester: next })
        .eq('id', user.id);
      if (updateError) throw updateError;
      setSuccess(next ? '테스터로 지정했습니다.' : '테스터 지정을 해제했습니다.');
      fetchUsers();
    } catch (err) {
      console.error('Failed to toggle tester:', err);
      setError('테스터 설정 변경에 실패했습니다: ' + (err.message || err));
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'name', headerName: '이름', width: 150 },
    { field: 'email', headerName: '이메일', width: 200 },
    { field: 'referral_code', headerName: '추천인 코드', width: 130 },
    {
      field: 'points',
      headerName: '포인트',
      width: 120,
      valueFormatter: (value) => value?.toLocaleString() || '0',
    },
    {
      field: 'unopened_eggs',
      headerName: '미개봉 등껍질',
      width: 130,
    },
    {
      field: 'event_tickets',
      headerName: '응모권',
      width: 100,
    },
    {
      field: 'created_at',
      headerName: '가입일',
      width: 160,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '',
    },
    {
      // 테스터로 지정된 계정만 앱의 '테스터 도구' 메뉴와 출금 최소금액 우회가 열린다.
      field: 'is_tester',
      headerName: '테스터',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Chip label="테스터" size="small" color="warning" />
        ) : null,
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 260,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handlePointAdjust(params.row)}
          >
            포인트 조정
          </Button>
          <Button
            variant="outlined"
            size="small"
            color={params.row.is_tester ? 'warning' : 'inherit'}
            onClick={() => handleToggleTester(params.row)}
          >
            {params.row.is_tester ? '테스터 해제' : '테스터 지정'}
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        사용자 관리
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        {!loading && users.length > 0 && (
          <DataGrid
            rows={users}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            columnVisibilityModel={{ id: false }}
          />
        )}
        {!loading && users.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">사용자가 없습니다.</Typography>
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>포인트 조정</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            사용자: {selectedUser?.name} ({selectedUser?.email})
            <br />
            현재 포인트: {selectedUser?.points?.toLocaleString()}
          </Typography>

          <TextField
            fullWidth
            label="조정 포인트 (음수는 차감)"
            type="number"
            value={pointAmount}
            onChange={(e) => setPointAmount(e.target.value)}
            margin="normal"
            placeholder="예: 1000 또는 -500"
          />

          <TextField
            fullWidth
            label="메모"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder="조정 사유를 입력하세요"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSubmitPointAdjust}>
            적용
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
